import { isObject } from "../../../common/tools.js";
import { win } from "../../../common/namespaces.js";
import {
  updateCurrentNavigationSignals,
  dispatchOnNavigate,
  getCurrentNavigation,
  provideNavigationInfo,
} from "./navigation.client.js";
import { asyncScope, NAV_HISTORY, type ObjectToEntries } from "./utils.js";
import type { HistoryPatched, HistoryThis, InvocationContext } from "./history.types.js";
import { sanitizeIncommingState } from "../state/state.utils.js";
import type { Debug } from "../../../exports/debug.js";
import type { NavigationType } from "./navigation.types.js";
import { otto } from "../../../exports/nexus.js";
import { noop } from "../../../common/function.js";

const log = asyncScope.scope("history");
const pushLog = log.scope("pushState");
const replaceLog = log.scope("replaceState");
const forwardLog = log.scope("forward");
const backLog = log.scope("back");
const goLog = log.scope("go");

/**
 *
 *
 */
export const $context = Symbol("context");

/**
 *
 *
 *
 *
 *
 */
export const getContext = (obj: unknown): InvocationContext =>
  isObject(obj) && $context in obj && isObject(obj[$context])
    ? { ...obj[$context], isContext: true }
    : { isContext: false };

/**
 *
 */
export const defineContext = (context: Omit<InvocationContext, "isContext">): HistoryThis =>
  ({ [$context]: context }) as HistoryThis;

export const logInvocation = <O, D>(logger: Debug, options: O, data: D): void => {
  logger.info("with options", options);
  logger.debug("with data", data);
  logger.trace(JSON.stringify(data));
};

/**
 *
 */
export const getPushStateHandler = (
  history: History,
): ((this: HistoryThis, data: unknown, unused: string, url?: string | URL | null) => void) => {
  const pushStateCall = history.pushState.bind(history);
  const type: NavigationType = "push";

  log.debug("Create pushState handler");
  return async function pushStateHandler(this, newData, unused, newUrl) {
    const { scope, clear, info, isContext } = getContext(this);
    logInvocation(pushLog, { scope, info, clear }, { data: newData, url: newUrl });

    /*                                                               */
    /*                                                                 */
    const stateData = sanitizeIncommingState(isContext, history.state, newData, clear);

    /*                                                */
    const [cancelled, cancellationReason, complete] = dispatchOnNavigate(
      info,
      true,
      type,
      getCurrentNavigation(newUrl, undefined, undefined, stateData),
    );
    if (cancelled) return log.info("Navigation was cancelled. Reason:", cancellationReason);

    /*                              */
    pushStateCall(stateData, unused, newUrl);

    /*                                       */
    updateCurrentNavigationSignals(info, type, getCurrentNavigation(newUrl));

    /*                                                                       */
    complete();
  };
};

/**
 *
 */
export const getReplaceStateHandler = (
  history: History,
): ((this: HistoryThis, data: unknown, unused: string, url?: string | URL | null) => void) => {
  const replaceStateCall = history.replaceState.bind(history);
  const type: NavigationType = "replace";

  log.debug("Create replaceState handler");
  return function replaceStateHandler(this, newData, unused, newUrl) {
    const { scope, clear, info, isContext } = getContext(this);
    logInvocation(replaceLog, { scope, info, clear }, { data: newData, url: newUrl });

    /*                                                               */
    /*                                                                 */
    const stateData = sanitizeIncommingState(isContext, history.state, newData, clear);

    /*                                                */
    const [cancelled, cancellationReason, complete] = dispatchOnNavigate(
      info,
      true,
      type,
      getCurrentNavigation(newUrl, undefined, undefined, stateData),
    );
    if (cancelled) return log.info("Navigation was cancelled. Reason:", cancellationReason);

    /*                              */
    replaceStateCall(stateData, unused, newUrl);

    /*                                       */
    updateCurrentNavigationSignals(info, type, getCurrentNavigation(newUrl));

    /*                                                                       */
    complete();
  };
};

/**
 *
 */
export const getForwardHandler = (history: History): ((this: HistoryThis) => void) => {
  const forward = history.forward.bind(history);

  log.debug("Create forward handler");
  return function forwardHandler(this) {
    const { scope, info } = getContext(this);
    logInvocation(forwardLog, { scope, info }, {});

    /*                                                         */
    provideNavigationInfo(info);

    /*                              */
    forward();
  };
};

/**
 *
 */
export const getBackHandler = (history: History): ((this: HistoryThis) => void) => {
  const back = history.back.bind(history);

  log.debug("Create back handler");
  return function backHandler(this) {
    const { scope, info } = getContext(this);
    logInvocation(backLog, { scope, info }, {});

    /*                                                         */
    provideNavigationInfo(info);

    /*                              */
    back();
  };
};

/**
 *
 */
export const getGoHandler = (history: History): ((this: HistoryThis, delata?: number) => void) => {
  const go = history.go.bind(history);

  log.debug("Create go handler");
  return function goHandler(this, delta) {
    const { scope, info } = getContext(this);
    logInvocation(goLog, { scope, info, delta }, {});

    /*                                                         */
    provideNavigationInfo(info);

    /*                              */
    go(delta);
  };
};

/**
 *
 */
export const historyPatched = otto.navigation[NAV_HISTORY];

/**
 *
 *
 *
 *
 *
 */
export const initNativeHistory = (history = win.history): (() => HistoryPatched) => {
  /*                                     */
  if (import.meta.env.SSR) return noop;

  log.debug("Patch history object");
  Object.defineProperties(
    history,
    Object.fromEntries(
      (
        [
          ["pushState", getPushStateHandler(history)],
          ["replaceState", getReplaceStateHandler(history)],
          ["forward", getForwardHandler(history)],
          ["back", getBackHandler(history)],
          ["go", getGoHandler(history)],
        ] satisfies ObjectToEntries<History>
      ).map(([key, value]) => [key, { value, writable: true, configurable: true }]),
    ),
  );

  log.debug("History object patched and set");
  return () => history as HistoryPatched;
};
